import { useEffect, useState } from 'react';
import Button from './Button';
import Modal from './Modal';

interface Request {
  message: string;
  action: string;
  input?: string;
  resolve: (answer: string | null) => void;
}

/** Actions that lose something get the brick button; everything else is the ordinary main action. */
const DESTRUCTIVE =
  /\b(delete|remove|archive|cancel|revoke|unlink|detach|erase|discard|clear|deactivate|merge|withdraw|drop|purge|undo)\b/i;

let open: ((r: Request) => void) | null = null;

const ask = (r: Omit<Request, 'resolve'>) =>
  new Promise<string | null>((resolve) => {
    if (open) open({ ...r, resolve });
    else resolve(null);
  });

/** Asks in a dialog on the page; resolves true only when the person confirms. Name the verb ("Delete the session"). */
export async function confirmAction(message: string, action = 'Confirm'): Promise<boolean> {
  return (await ask({ message, action })) !== null;
}

/** Asks for a typed answer in a dialog on the page; resolves null when cancelled. */
export function promptAction(message: string, initial = '', action = 'Save'): Promise<string | null> {
  return ask({ message, action, input: initial });
}

export function ConfirmHost() {
  const [request, setRequest] = useState<Request | null>(null);
  const [value, setValue] = useState('');
  useEffect(() => {
    open = (r) => {
      setValue(r.input ?? '');
      setRequest(r);
    };
    return () => {
      open = null;
    };
  }, []);
  if (!request) return null;
  const answer = (result: string | null) => {
    request.resolve(result);
    setRequest(null);
  };
  const asking = request.input !== undefined;
  const destructive = !asking && DESTRUCTIVE.test(`${request.action} ${request.message}`);
  return (
    <div className="relative z-[70]">
      <Modal
        title={asking ? 'One detail' : 'Are you sure?'}
        width={460}
        onClose={() => answer(null)}
        footer={
          <>
            <Button variant={destructive ? 'destructive' : 'primary'} onClick={() => answer(asking ? value : '')}>
              {request.action}
            </Button>
            <Button variant="secondary" onClick={() => answer(null)}>
              Cancel
            </Button>
          </>
        }
      >
        <p className="text-[15px] leading-[22px] text-ink">{request.message}</p>
        {asking ? (
          <input
            autoFocus
            aria-label={request.message}
            className="border border-line-strong rounded-sm px-3 py-[10px] min-h-[44px] mt-3 w-full text-[15px] text-ink"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        ) : null}
      </Modal>
    </div>
  );
}
